<template>
  <div
    class="kb-field generic-select"
    :class="{
      'kb-field--active': isOpen || hasValue,
    }"
    :data-entity-type="entityType"
    :data-entity-id="componentId"
  >
    <div class="kb-field__wrap">
      <v-select ref="vSelectRef" v-model="selectedItem" :options="itemOptions" :filterable="false"
        :appendToBody="appendToBody"
          :reduce="item => item[valueField]" :label="labelField" @search="onSearch" @open="onOpen" @input="onItemSelect" @update:modelValue="onItemSelect"
        @keydown="onKeydown" @clear="onClear" @close="onClose" :placeholder="effectivePlaceholder" :loading="isLoading"
        :clearable="clearable" :multiple="multiple" :dir="dir">
      <template #spinner="{ loading }">
        <div v-if="loading" class="vs__spinner">
          {{ loadingText }}
        </div>
      </template>

      <template #no-options>
        <div class="text-center">
          {{ store.searchQuery ? noResultsText : startTypingText }}
        </div>
      </template>

      <template #list-footer>
        <div v-if="hasMoreItems" class="infinite-scroll-footer">
          <button v-if="!isLoadingMore" @click="loadMore" class="load-more-btn" type="button">
            بارگذاری بیشتر
          </button>
          <div v-else class="loading-more">
            در حال بارگذاری...
          </div>
        </div>
        <div v-else-if="store.itemsList.length > 0" class="no-more-items">
          مورد بیشتری برای نمایش وجود ندارد
        </div>
      </template>

      <template #option="option">
        <slot name="option" v-bind="option">
          <div class="default-option">
            <div class="option-main">
              <strong>{{ option[labelField] }}</strong>
              <span v-if="option[secondaryField]" class="secondary-text">
                {{ option[secondaryField] }}
              </span>
            </div>
            <div v-if="option[descriptionField]" class="option-description">
              {{ option[descriptionField] }}
            </div>
          </div>
        </slot>
      </template>
      </v-select>

      <label v-if="label" class="kb-field__label">
        {{ label }} <span v-if="required" class="kb-field__req">*</span>
      </label>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from 'vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

// eslint-disable-next-line no-undef
const props = defineProps({
  modelValue: {
    type: [String, Number, Array],
    default: null
  },
  label: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  entityType: {
    type: String,
    required: true
  },
  store: {
    type: Object,
    required: true
  },
  labelField: {
    type: String,
    default: 'label'
  },
  valueField: {
    type: String,
    default: 'id'
  },
  secondaryField: {
    type: String,
    default: ''
  },
  descriptionField: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'جستجو و انتخاب...'
  },
  loadingText: {
    type: String,
    default: 'در حال جستجو...'
  },
  noResultsText: {
    type: String,
    default: 'نتیجه‌ای یافت نشد'
  },
  startTypingText: {
    type: String,
    default: 'برای جستجو تایپ کنید'
  },
  clearable: {
    type: Boolean,
    default: true
  },
  multiple: {
    type: Boolean,
    default: false
  },
  fetchOnOpen: {
    type: Boolean,
    default: true
  },
  appendToBody: {
    type: Boolean,
    default: false
  },
  enableInfiniteScroll: {
    type: Boolean,
    default: false
  },
  enableEnterSearch: {
    type: Boolean,
    default: true
  },
  refetchOnClear: {
    type: Boolean,
    default: true
  },
  dir: {
    type: String,
    default: 'rtl'
  }
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['update:modelValue', 'item-selected', 'search', 'open', 'load-more', 'clear']);

// Reactive state
const isLoadingMore = ref(false);
const vSelectRef = ref(null);
const isOpen = ref(false);
const componentId = ref(`${props.entityType}-${Math.random().toString(36).substr(2, 6)}`);
const dropdownListboxId = ref(null);
const dropdownObserver = ref(null);
const positionedAppendedDropdownId = ref(null);
const rafIdRef = ref(null);

// Reposition appended-dropdown utilities
const positionAppendedDropdown = (ownId) => {
  if (!ownId) return;
  try {
    const ddEl = document.getElementById(ownId);
    const toggleEl = vSelectRef.value?.$refs?.toggle;
    if (!ddEl || !toggleEl) return;

    const toggleRect = toggleEl.getBoundingClientRect();
    const left = toggleRect.left + window.scrollX;
    const top = toggleRect.bottom + window.scrollY;

    ddEl.style.position = 'absolute';
    ddEl.style.left = `${Math.max(8, Math.round(left))}px`;
    ddEl.style.top = `${Math.round(top)}px`;
    ddEl.style.minWidth = `${Math.round(toggleRect.width)}px`;
    ddEl.style.zIndex = '9999';

    // Ensure dropdown doesn't overflow viewport on the right
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const ddWidth = ddEl.offsetWidth || parseFloat(getComputedStyle(ddEl).width) || toggleRect.width;
    if (left + ddWidth > viewportWidth) {
      const newLeft = Math.max(8, viewportWidth - ddWidth - 8);
      ddEl.style.left = `${Math.round(newLeft)}px`;
    }

    positionedAppendedDropdownId.value = ownId;
  } catch (e) {
    // Ignore any errors from measurement, as DOM may not be ready
  }
};

const repositionHandler = () => {
  if (rafIdRef.value) cancelAnimationFrame(rafIdRef.value);
  rafIdRef.value = requestAnimationFrame(() => {
    if (!dropdownListboxId.value) return;
    positionAppendedDropdown(dropdownListboxId.value);
    rafIdRef.value = null;
  });
};

const addRepositionListeners = () => {
  try {
    window.addEventListener('resize', repositionHandler, { passive: true });
    window.addEventListener('scroll', repositionHandler, true);
  } catch (e) { /* ignore */ }
};

const removeRepositionListeners = () => {
  try {
    window.removeEventListener('resize', repositionHandler, { passive: true });
    window.removeEventListener('scroll', repositionHandler, true);
    if (rafIdRef.value) {
      cancelAnimationFrame(rafIdRef.value);
      rafIdRef.value = null;
    }
  } catch (e) { /* ignore */ }
};

// Initialization

// Computed
const selectedItem = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);

    if (value === null && props.refetchOnClear) {
      handleClear();
    }
  }
});

const itemOptions = computed(() => {
  const items = props.store.itemsList || [];
  // computed items
  return items.map(item => ({
    value: item[props.valueField],
    label: item[props.labelField],
    ...item
  }));
});

const hasValue = computed(() => {
  const v = props.modelValue;
  if (props.multiple) return Array.isArray(v) && v.length > 0;
  return v !== null && v !== undefined && v !== '';
});

const effectivePlaceholder = computed(() => {
  // Match FloatingSelect/HierarchySelect behaviour: placeholder only when focused/open
  // so label can sit in the input area when idle.
  return isOpen.value ? props.placeholder : '';
});

const hasMoreItems = computed(() => {
  return props.store.pagination &&
    props.store.pagination.current_page < props.store.pagination.last_page;
});

const isLoading = computed(() => props.store.isLoading || false);

// Methods
// در GenericSelect
const localSearchQuery = ref('');

const onSearch = async (query) => {
  // handle search

  // ذخیره در متغیر داخلی
  localSearchQuery.value = query;

  if (props.enableEnterSearch) {
    // Enter search enabled - waiting for Enter key
    return;
  }

  // Performing immediate search
  await performSearch(query);
};

const performSearch = async (query = null) => {
  const searchQuery = query || localSearchQuery.value;
  // performing search with query

  try {
    // ارسال به store برای جستجو
    await props.store.searchItems(searchQuery);
    emit('search', searchQuery);
  } catch (error) {
    console.error(`[${componentId.value}] Search error:`, error);
  }
};

const onKeydown = (event) => {
  // keydown

  if (props.enableEnterSearch && event.key === 'Enter') {
    event.preventDefault();
    event.stopPropagation();
  // Enter pressed, performing search
    performSearch();
  }
};
const onClear = () => {
  // Clear clicked
  emit('clear');
  // Ensure parent also knows the selection was cleared
  try {
    emit('item-selected', null);
    lastEmittedValue.value = null;
  } catch (e) { /* ignore if not handled */ }
  if (props.refetchOnClear) {
    handleClear();
  }
};


const handleClear = async () => {
  // Handling clear
  await props.store.loadInitialData();
};

const onOpen = async () => {
  // Dropdown opening
  isOpen.value = true;
  emit('open');

  if (props.fetchOnOpen && (!props.store.itemsList || props.store.itemsList.length === 0)) {
  // Fetching initial data on open
    await props.store.loadInitialData();
  }

  // اضافه کردن listener برای تشخیص کلیک خارج
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside);
  }, 100);

  nextTick(() => {
  // فوکوس روی input جستجو (استفاده از شناسه‌ی منحصر به فرد برای هر instance)
  const searchInput = document.querySelector(`[data-entity-id="${componentId.value}"] .vs__search`);
    if (searchInput) {
  // Focusing search input
      searchInput.focus();
    } else {
      console.warn(`[${componentId.value}] Search input not found`);
    }
  });
  // store associated dropdown id (if any) so we can ignore clicks inside appended dropdown
  nextTick(() => {
    try {
      const toggleEl = vSelectRef.value?.$refs?.toggle;
      if (toggleEl && toggleEl.getAttribute) {
        const ownId = toggleEl.getAttribute('aria-owns');
        dropdownListboxId.value = ownId;
        // add theme-aware class to appended dropdown element if present
        const ddEl = ownId ? document.getElementById(ownId) : null;
        if (ddEl && !ddEl.classList.contains('theme-aware')) {
          ddEl.classList.add('theme-aware');
          // Position appended dropdown and start listening for scroll/resize
          if (props.appendToBody) {
            positionAppendedDropdown(ownId);
            addRepositionListeners();
          }
        } else if (ownId) {
          // If dropdown appended to body isn't present yet, retry a few times
          let retries = 0;
          const maxRetries = 6;
          const intervalId = setInterval(() => {
            const el = document.getElementById(ownId);
            if (el) {
              if (!el.classList.contains('theme-aware')) el.classList.add('theme-aware');
              if (props.appendToBody) {
                positionAppendedDropdown(ownId);
                addRepositionListeners();
              }
              clearInterval(intervalId);
            }
            retries++;
            if (retries >= maxRetries) {
              clearInterval(intervalId);
            }
          }, 50);
        }
        // Start a mutation observer to ensure we reapply theme-aware class if vue-select recreates the appended dropdown
        try {
          if (!dropdownObserver.value) {
            const observer = new MutationObserver((mutationsList) => {
              for (const mut of mutationsList) {
                for (const node of mut.addedNodes) {
                  if (!(node instanceof HTMLElement)) continue;
                  if (!ownId) continue;
                    if (node.id === ownId && node.classList.contains('vs__dropdown-menu')) {
                    if (!node.classList.contains('theme-aware')) {
                      node.classList.add('theme-aware');
                      // observer: theme-aware class applied to dropdown
                      // Ensure it's positioned and reposition listeners are added
                      if (props.appendToBody) {
                        positionAppendedDropdown(ownId);
                        addRepositionListeners();
                      }
                    }
                  } else {
                    const found = node.querySelector ? node.querySelector(`#${ownId}`) : null;
                    if (found && found.classList.contains('vs__dropdown-menu')) {
                      if (!found.classList.contains('theme-aware')) {
                        found.classList.add('theme-aware');
                        // observer: theme-aware class applied to dropdown
                        if (props.appendToBody) {
                          positionAppendedDropdown(ownId);
                          addRepositionListeners();
                        }
                      }
                    }
                  }
                }
              }
            });
            observer.observe(document.body, { childList: true, subtree: true });
            dropdownObserver.value = observer;
          }
        } catch (e) {
          // ignore observer errors on older browsers/environments
        }
        // If not appended to body (appendToBody=false), ensure any inline dropdown gets the theme-aware class as well
        if (!props.appendToBody) {
          try {
            const wrapper = vSelectRef.value?.$el;
            const localDropdown = wrapper ? wrapper.querySelector('.vs__dropdown-menu') : null;
            if (localDropdown && !localDropdown.classList.contains('theme-aware')) {
              localDropdown.classList.add('theme-aware');
              // theme-aware class applied to local dropdown via wrapper
            }
          } catch (e) { /* ignore */ }
        }
      }
    } catch (e) {
      // ignore
    }
  });
};

const onClose = () => {
  // Dropdown closing
  isOpen.value = false;
  document.removeEventListener('click', handleClickOutside);
  // remove theme-aware class from appended dropdown
  if (dropdownListboxId.value) {
    const ddEl = document.getElementById(dropdownListboxId.value);
    if (ddEl && ddEl.classList.contains('theme-aware')) {
      ddEl.classList.remove('theme-aware');
    }
  }
  dropdownListboxId.value = null;
  // Ensure any repositioning listeners are removed and RAF cancelled
  removeRepositionListeners();
  positionedAppendedDropdownId.value = null;
  // Disconnect mutation observer if active
  if (dropdownObserver.value) {
    try { dropdownObserver.value.disconnect(); } catch (e) { /* ignore */ }
    dropdownObserver.value = null;
    // ensure reposition listeners are removed when observer disconnects
    removeRepositionListeners();
  }
};

  const handleClickOutside = (event) => {
  const selectElement = document.querySelector(`[data-entity-id="${componentId.value}"]`);
  const dropdownElement = dropdownListboxId.value ? document.getElementById(dropdownListboxId.value) : null;
  // ignore clicks inside the dropdown element itself (when appended to body)
  if (dropdownElement && dropdownElement.contains(event.target)) {
    return;
  }
  if (selectElement && !selectElement.contains(event.target)) {
  // Click outside detected, closing dropdown
    if (vSelectRef.value && typeof vSelectRef.value.close === 'function') {
      vSelectRef.value.close();
    }
    // Ensure our state is updated and listener removed
    isOpen.value = false;
    document.removeEventListener('click', handleClickOutside);
  }
};

const onItemSelect = async (item) => {
  let selected = null;
  try {
    // If v-select returns a full object, use it directly
    if (item && typeof item === 'object') {
      selected = item;
    } else {
      // If v-select returns a reduced value (e.g. id), find the full record in the store
      // Try store-provided finder first
      if (props.store && typeof props.store.findItemById === 'function') {
        selected = props.store.findItemById(item) || null;
      }
      // If not found, try using the configured valueField
      if (!selected) {
        selected = props.store.itemsList?.find(i => i[props.valueField] === item) || null;
      }
      // If still not found and itemsList is empty or loading, try loading initial data then search again
      if (!selected && props.store && props.store.itemsList && props.store.itemsList.length === 0 && typeof props.store.loadInitialData === 'function') {
        try {
          await props.store.loadInitialData();
          selected = props.store.itemsList?.find(i => i[props.valueField] === item) || null;
        } catch (loadErr) {
          // loadInitialData failed while resolving selection
        }
      }
    }
  } catch (e) {
    console.error(`[${componentId.value}] Error while resolving selected item:`, e);
    selected = null;
  }
  const selectedId = selected ? selected[props.valueField] : null;
  // Prevent duplicate emits if the same value was already emitted (only when selectedId is not null)
  if (selectedId && lastEmittedValue.value === selectedId) {
    return;
  }
  if (selectedId) lastEmittedValue.value = selectedId;
  
  emit('item-selected', selected);
};

const loadMore = async () => {
  if (isLoadingMore.value || !hasMoreItems.value) return;

  isLoadingMore.value = true;
  try {
    await props.store.loadNextPage();
    emit('load-more', props.store.pagination.current_page);
  } catch (error) {
  console.error(`[${componentId.value}] Error loading more:`, error);
  } finally {
    isLoadingMore.value = false;
  }
};

// Cleanup
onUnmounted(() => {
  // Component unmounting
  document.removeEventListener('click', handleClickOutside);
  // cleanup any appended dropdown element class
  if (dropdownListboxId.value) {
    const ddEl = document.getElementById(dropdownListboxId.value);
    if (ddEl && ddEl.classList.contains('theme-aware')) {
      ddEl.classList.remove('theme-aware');
    }
  }
  dropdownListboxId.value = null;
  // Ensure listeners removed and RAF cancelled
  removeRepositionListeners();
  positionedAppendedDropdownId.value = null;
  // Disconnect mutation observer if active
  if (dropdownObserver.value) {
    try { dropdownObserver.value.disconnect(); } catch (e) { /* ignore */ }
    dropdownObserver.value = null;
    // ensure reposition listeners are removed when observer disconnects
    removeRepositionListeners();
  }
  if (props.store.cleanup) {
    props.store.cleanup();
  }
});

// دیباگ: لاگ کردن تغییرات مهم
const lastEmittedValue = ref(null);
watch(() => props.modelValue, (newValue) => {
  // When modelValue changes from outside, ensure we emit item-selected with the resolved record
  // but avoid duplicate emits
  try {
    onItemSelect(newValue);
  } catch (e) {
    console.error(`[${componentId.value}] Error in modelValue watcher onItemSelect:`, e);
  }
});

watch(() => props.store.itemsList, () => {
  // store itemsList changed
}, { deep: true });
</script>

<style scoped>
.kb-field {
  width: 100%;
}

.kb-field__wrap {
  position: relative;
}

.kb-field__label {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  padding: 0 6px;
  border-radius: 8px;
  color: var(--text-color-secondary, var(--muted));
  font-weight: 800;
  font-size: .9rem;
  pointer-events: none;
  transition: all .16s ease;
  background: transparent;

  display: inline-flex;
  align-items: center;
  gap: 4px;
  direction: rtl;
}

.kb-field--active .kb-field__label {
  top: 0;
  transform: translateY(-50%);
  font-size: .78rem;
  color: var(--background-color-button, var(--primary));
  background: var(--background-color, var(--surface));
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
}

.kb-field__req {
  color: var(--danger, #dc3545);
  -webkit-text-fill-color: var(--danger, #dc3545);
  margin: 0;
}

.generic-select {
  position: relative; /* ensure local dropdowns position absolutely relative to this container */
}

.default-option {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.default-option:hover {
  background-color: #f5f5f5;
}

.option-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.secondary-text {
  font-size: 0.8em;
  color: #666;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
}

.option-description {
  font-size: 0.8em;
  color: #888;
}

.text-center {
  text-align: center;
  padding: 10px;
  color: #666;
}

.infinite-scroll-footer {
  padding: 10px;
  text-align: center;
  border-top: 1px solid #f0f0f0;
}

.load-more-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
}

.load-more-btn:hover {
  background: #0056b3;
}

.loading-more {
  padding: 8px;
  color: #666;
  font-size: 0.9em;
}

.no-more-items {
  padding: 6px 6px; /* slightly reduced vertical/horizontal padding */
  color: #999;
  font-size: 0.7rem; /* make the text smaller */
  text-align: center;
}

:deep(.vs__dropdown-toggle) {
  /* Match .form-group input/select style so the select toggle looks like a standard form control */
  border: 1px solid var(--text-color-secondary) !important;
  border-radius: 10px;
  background: var(--background-color) !important;
  color: var(--text-color-primary) !important;
  padding: 6px 52px 6px 12px; /* space for floating label (RTL) */
  height: 34px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
}

/* Visual focus state to follow the app's primary accent color */
:deep(.vs__dropdown-toggle).vs--open,
:deep(.vs__dropdown-toggle):focus-within,
:deep(.vs__dropdown-toggle):hover {
  border-color: var(--text-color-primary) !important;
  box-shadow: 0 0 0 0.2rem rgba(var(--background-color-button-rgb), 0.12) !important;
}

:deep(.vs__search) {
  direction: rtl;
  text-align: right;
  font-size: 14px;
}

:deep(.vs__clear) {
  margin-left: 8px;
}

:deep(.vs__dropdown-menu) {
  max-height: 200px;
  overflow-y: auto;
  /* Make sure dropdown appears above nearby containers */
  position: absolute;
  z-index: 9999;
  border: 1px solid var(--text-color-secondary) !important;
  border-radius: 10px;
}

/* Make sure selected text follows the app theme's colors (dark/light) */
:deep(.vs__dropdown-toggle) {
  color: var(--text-color-primary) !important;
  background: var(--background-color) !important;
}

:deep(.vs__dropdown-toggle) * {
  color: var(--text-color-primary) !important;
}

:deep(.vs__dropdown-toggle) svg,
:deep(.vs__dropdown-toggle) svg path {
  fill: var(--text-color-primary) !important;
}

/* The selected value displayed inside the toggle */
:deep(.vs__selected) {
  color: var(--text-color-primary) !important;
}
:deep(.vs__selected-options) {
  display: inline-block;
}

/* Placeholder color when no item is selected */
:deep(.vs__placeholder) {
  color: var(--text-color-secondary) !important;
}

/* Single/multiple selected tags */
:deep(.vs__tag) {
  background: rgba(var(--text-color-secondary-rgb), 0.06) !important;
  color: var(--text-color-primary) !important;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.12) !important;
}

/* Dropdown option colors */
:deep(.vs__dropdown-option) {
  color: var(--text-color-primary) !important;
  background: var(--background-color) !important;
}

:deep(.vs__dropdown-menu) * {
  color: var(--text-color-primary) !important;
}

/* Highlighted option on hover/focus */
:deep(.vs__dropdown-option--highlight), :deep(.vs__dropdown-option--selected) {
  background: var(--background-color-secondary) !important;
  color: var(--text-color-primary) !important;
}
</style>